import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import api, { setApiChurchFilter } from '../api/axios';
import { useAuth } from './AuthContext';

const ChurchContext = createContext(null);

const STORAGE_KEY = 'superadmin_selected_church_id';

export function ChurchProvider({ children }) {
  const { user, loading } = useAuth();
  const isPlatformAdmin = user?.role === 'superadmin_platform';
  const isSupervisor = user?.role === 'superviseur';
  // Les deux rôles basculent entre plusieurs églises via le même sélecteur ;
  // seul le superadmin a en plus un mode "vue globale" (selectedChurchId null).
  const canSwitchChurch = isPlatformAdmin || isSupervisor;

  const [churches, setChurches] = useState([]);

  // Initialiser depuis localStorage — jamais null si une valeur est sauvegardée
  const [selectedChurchId, setSelectedChurchIdState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : null;
  });

  // Wrapper : persiste dans localStorage UNIQUEMENT pour le superadmin
  const setSelectedChurchId = useCallback((id, persist = true) => {
    setSelectedChurchIdState(id);
    if (persist) {
      if (id === null) {
        localStorage.setItem(STORAGE_KEY, 'global');
      } else {
        localStorage.setItem(STORAGE_KEY, String(id));
      }
    }
  }, []);

  const fetchChurches = useCallback(async (allowGlobal = true) => {
    try {
      const res = await api.get('/churches/');
      const list = res.data.results || res.data;
      setChurches(list);
      if (list.length === 0) return;

      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'global' && allowGlobal) {
        // Mode config globale — on garde null (superadmin uniquement)
        setSelectedChurchIdState(null);
      } else if (saved && saved !== 'global') {
        const stillExists = list.some(c => c.id === parseInt(saved, 10));
        if (!stillExists) {
          setSelectedChurchId(list[0].id);
        }
      } else {
        setSelectedChurchId(list[0].id);
      }
    } catch {
      // ignore
    }
  }, [setSelectedChurchId]);

  useEffect(() => {
    // Attendre que l'auth soit résolue avant d'agir
    if (loading) return;

    if (canSwitchChurch) {
      fetchChurches(isPlatformAdmin);
    } else if (user) {
      // Admin normal : utiliser son église, sans toucher au localStorage superadmin
      setSelectedChurchIdState(user.church_id || null);
    }
  }, [loading, canSwitchChurch, isPlatformAdmin, user, fetchChurches]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedChurch = churches.find(c => c.id === selectedChurchId) || null;
  // Le superviseur n'a jamais de vue globale (contrairement au superadmin) :
  // s'il n'a encore rien sélectionné, activeChurchId reste null jusqu'à ce
  // que fetchChurches() fixe automatiquement la première église assignée.
  const activeChurchId = canSwitchChurch ? selectedChurchId : (user?.church_id || null);

  // Keep axios interceptor in sync with selected church
  useEffect(() => {
    setApiChurchFilter(activeChurchId, canSwitchChurch);
  }, [activeChurchId, canSwitchChurch]);

  return (
    <ChurchContext.Provider value={{
      churches,
      selectedChurchId,
      setSelectedChurchId,
      selectedChurch,
      activeChurchId,
      isPlatformAdmin,
      isSupervisor,
      canSwitchChurch,
      refetchChurches: fetchChurches,
    }}>
      {children}
    </ChurchContext.Provider>
  );
}

export function useChurch() {
  const ctx = useContext(ChurchContext);
  if (!ctx) throw new Error('useChurch must be used within ChurchProvider');
  return ctx;
}

export default ChurchContext;
